// useApi.js - Updated to use ApiService
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useStatusModal } from "../context/StatusModalContext";
import { apiService } from "../services/apiService";

const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { showStatusModal } = useStatusModal();
  const { t } = useTranslation();

  // Override error messages for the service
  const setupErrorMessages = () => {
    apiService.getErrorMessages = () => ({
      error_session_expired: t("error_session_expired"),
      error_server: t("error_server"),
      error_something_wrong: t("error_something_wrong"),
    });
  };

  const apiCallWrapper = useCallback(
    async (apiMethod, ...args) => {
      setLoading(true);
      setError(null);
      setupErrorMessages();

      try {
        const result = await apiMethod(...args);
        return result;
      } catch (err) {
        setError(err.message);
        const isRefreshError = err.code === 999;
        const redirectionUrl = isRefreshError ? "/" : null;
        showStatusModal(err.message, redirectionUrl, true, isRefreshError);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [showStatusModal, t],
  );

  const get = useCallback(
    (endpoint, options = {}) =>
      apiCallWrapper(apiService.get.bind(apiService), endpoint, options),
    [apiCallWrapper],
  );

  const post = useCallback(
    (endpoint, body, options = {}) =>
      apiCallWrapper(apiService.post.bind(apiService), endpoint, body, options),
    [apiCallWrapper],
  );

  const put = useCallback(
    (endpoint, body, options = {}) =>
      apiCallWrapper(apiService.put.bind(apiService), endpoint, body, options),
    [apiCallWrapper],
  );

  const patch = useCallback(
    (endpoint, body, options = {}) =>
      apiCallWrapper(
        apiService.patch.bind(apiService),
        endpoint,
        body,
        options,
      ),
    [apiCallWrapper],
  );

  const del = useCallback(
    (endpoint, options = {}) =>
      apiCallWrapper(apiService.del.bind(apiService), endpoint, options),
    [apiCallWrapper],
  );

  return { get, post, put, patch, del, loading, error };
};

export default useApi;
